import { useEffect, useRef } from 'react';
import type { AddressSuggestion, OrderLocation } from '../../types';
import Icon from '../Icon';
import { Spinner } from '../Spinner';
import { cn } from '../cn';
import { useAddressAutocomplete, type AddressAutocompleteSource } from './useAddressAutocomplete';

export type { AddressAutocompleteSource } from './useAddressAutocomplete';

export interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  /** Resolved location of a picked suggestion, or `null` once the text is edited manually. */
  onLocationChange: (location: OrderLocation | null) => void;
  onSuggestionChange?: (suggestion: AddressSuggestion | null) => void;
  source: AddressAutocompleteSource;
  placeholder?: string | undefined;
  id?: string | undefined;
  lang?: string | undefined;
  loadingLabel?: string | undefined;
  /** Overrides for the textarea surface (background, radius, focus styles). */
  inputClassName?: string | undefined;
  /** Overrides for the suggestions popover surface. */
  listClassName?: string | undefined;
}

export default function AddressAutocomplete({
  value,
  onChange,
  onLocationChange,
  onSuggestionChange,
  source,
  placeholder,
  id,
  lang,
  loadingLabel,
  inputClassName,
  listClassName,
}: AddressAutocompleteProps) {
  const {
    suggestions,
    isLoading,
    showSuggestions,
    onInputChange,
    onSuggestionSelect,
    dismissSuggestions,
  } = useAddressAutocomplete(value, onChange, {
    source,
    lang,
    onLocationChange,
    onSuggestionChange,
  });

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        dismissSuggestions();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dismissSuggestions]);

  return (
    <div className="relative w-full" ref={containerRef}>
      <div className="relative">
        <textarea
          id={id}
          className={cn(
            'w-full resize-none border-none rounded-[var(--radius-xl)] bg-primary p-4 font-sans text-base text-text transition-all placeholder:text-text-secondary focus:bg-primary focus:shadow-[0_0_0_2px_var(--color-focus-ring)] focus:outline-none',
            inputClassName,
          )}
          value={value}
          onChange={(event) => onInputChange(event.target.value)}
          placeholder={placeholder}
          rows={3}
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={showSuggestions}
        />
        {isLoading ? (
          <span className="absolute right-3.5 top-1/2 flex -translate-y-1/2 items-center text-text-secondary">
            <Spinner size="sm" label={loadingLabel ?? 'Loading suggestions'} />
          </span>
        ) : null}
      </div>

      {showSuggestions ? (
        <ul
          className={cn(
            'absolute left-0 right-0 top-[calc(100%+4px)] z-[100] m-0 flex max-h-[220px] list-none flex-col gap-1 overflow-y-auto overscroll-contain rounded-[var(--radius-xl)] border border-border bg-primary p-1.5 shadow-lg',
            listClassName,
          )}
          role="listbox"
        >
          {suggestions.map((suggestion) => (
            <li key={suggestion.place_id} role="option" aria-selected={false} className="m-0">
              <button
                type="button"
                className="flex w-full cursor-pointer items-start gap-2.5 rounded-[var(--radius-lg)] border-none bg-transparent p-3 text-left text-text transition-colors hover:bg-secondary focus-visible:bg-secondary active:bg-surface-high"
                onClick={() => onSuggestionSelect(suggestion)}
              >
                <Icon
                  name="location_on"
                  className="mt-0.5 shrink-0 text-base !leading-none text-accent"
                />
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium text-text">
                    {suggestion.address_line1 ?? suggestion.formatted}
                  </span>
                  {suggestion.address_line2 ? (
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[12px] text-text-secondary">
                      {suggestion.address_line2}
                    </span>
                  ) : null}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
